import type { CreditCard, Invoice } from '@domain/finance'
import { absCents } from '@shared/money/cents'
import { formatBRL } from '@shared/money/format'
import { Glass } from '@ui/glass/Glass'

/**
 * O card de fatura por cartão, no topo da tela de Transações.
 *
 * `Docs/04-Design/Telas/Transações.md` desenha exatamente isto: *"Cartão de
 * crédito (Nubank) / Fatura atual R$ 1.256,80 / Vencimento 10/08"*.
 *
 * ## O que ele NÃO mostra, e por quê
 *
 * **Não mostra "restante", nem "pago", nem estado de pagamento.** `paidCents`
 * está **declarado provisório** desde a decisão 140(e): ele agrupa pelo
 * `billForecastDate` do próprio pagamento — a fatura em que o pagamento *foi
 * lançado* —, e num cartão brasileiro o pagamento entra como crédito na fatura
 * **seguinte** à que ele quita. Medido na base real: **8 das 19** faturas ficam
 * com restante **positivo**, e 2025-10 tem total −15.912,12 com pago +19.099,70.
 *
 * Mostrar isso na tela transformaria um número que sabidamente não fecha em um
 * número que a pessoa acredita. `totalCents` **não** é afetado — consumo é
 * consumo —, e é por isso que o card existe assim mesmo, com o que está certo.
 * O resto entra quando a conferência manual da definição de pronto da Fase 3
 * acontecer.
 *
 * **Não é clicável.** A nota diz *"toque abre a fatura com as transações do
 * ciclo"*, e essa tela não existe. Um card que parecesse tocável e não fizesse
 * nada seria pior que um card estático.
 *
 * ## O valor sai em módulo, e a conversão é aqui
 *
 * `totalCents` vem negativo, no mesmo sinal das transações que ele soma — o
 * sinal mora no domínio e sai de lá intacto. É nesta fronteira que ele vira
 * "R$ 1.256,80". Mesma regra do gasto em `TransactionsList`, e o motivo é o da
 * decisão 97(c).
 *
 * ## Vidro
 *
 * `flat`, e não `lens`. `Docs/04-Design/Liquid Glass.md` limita a **3 `lens` por
 * tela**, e o chip de mês selecionado já usa um; o painel em volta já é `frost`,
 * então um segundo nível translúcido em cima dele empilharia composição sem
 * ganhar hierarquia. `flat` é sólido: separa o card do painel pelo contorno.
 */

/** `'2026-08-10'` → `'10/08'`. Vencimento não precisa do ano: ele é do mês. */
function shortDate(date: string): string {
  return `${date.slice(8, 10)}/${date.slice(5, 7)}`
}

function cardNames(creditCards: CreditCard[]): Map<string, string> {
  return new Map(creditCards.map((card) => [card.id, card.name]))
}

export function InvoiceCards({
  invoices,
  creditCards,
}: {
  invoices: Invoice[]
  creditCards: CreditCard[]
}) {
  if (invoices.length === 0) return null
  const names = cardNames(creditCards)

  return (
    <section aria-label="Faturas do mês" className="mb-4 flex flex-wrap gap-3">
      {invoices.map((invoice) => (
        <Glass key={invoice.id} level="flat" className="min-w-48 flex-1 rounded-xl p-3">
          <h3 className="text-xs text-[var(--text-muted)]">
            {/* Cartão que não existe mais continua tendo fatura: o documento
                guarda o id, e apagar o cartão não apaga o histórico. Dizer isso
                é melhor do que mostrar um card sem título. */}
            {names.get(invoice.creditCardId) ?? 'cartão que não existe mais'}
          </h3>
          <p className="text-lg font-semibold tabular-nums">
            {formatBRL(absCents(invoice.totalCents))}
          </p>
          <p className="text-xs text-[var(--text-muted)]">
            {`Vence ${shortDate(invoice.dueDate)} · ${String(invoice.transactionCount)} ${
              invoice.transactionCount === 1 ? 'transação' : 'transações'
            }`}
          </p>
        </Glass>
      ))}
    </section>
  )
}
